// src/pages/admin/AdminPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { InformationCircleIcon, TrashIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
// --- FIXED IMPORT PATHS ---
import ConfirmModal from '../../components/common/ConfirmModal.js'; // Added .js extension
import { useAuth } from '../../context/AuthContext.js'; // Added .js extension
import { parseSubmittedPaths, buildDownloadUrl } from '../../utils/userDocumentFiles';
// --- END FIX ---

// Renders one link per submitted file for a single user_required_documents row.
// Handles both JSON-array (multi-file) and plain string (legacy single-file) shapes.
const AdminDocumentLinks = ({ doc, approvedLabel = 'Approved', defaultLabel = 'User Submitted', emptyLabel = 'No File' }) => {
    const status = doc.status;
    const isViewable = status === 'submitted' || status === 'approved' || status === 'rejected';
    if (!isViewable) {
        return <span className="text-neutral-500 mr-3">{emptyLabel}</span>;
    }
    const paths = parseSubmittedPaths(doc.submitted_file_path);
    if (paths.length === 0) {
        return <span className="text-neutral-500 mr-3">{emptyLabel}</span>;
    }
    const baseLabel = status === 'approved' ? approvedLabel : defaultLabel;
    const linkClass = 'text-[#06b6d4] hover:text-[#22d3ee] hover:underline mr-3';
    if (paths.length === 1) {
        return (
            <a
                href={buildDownloadUrl(doc.user_document_id)}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
            >
                {baseLabel ? `View ${baseLabel} File` : 'View File'}
            </a>
        );
    }
    return (
        <span className="inline-flex flex-wrap gap-x-3 gap-y-1">
            {paths.map((_, i) => (
                <a
                    key={i}
                    href={buildDownloadUrl(doc.user_document_id, i)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                >
                    {baseLabel ? `View ${baseLabel} File ${i + 1}` : `View File ${i + 1}`}
                </a>
            ))}
        </span>
    );
};


// --- Define Updated Onboarding Steps (Unchanged) ---
const STEP_DEFINITIONS = {
    0: 'Step 0: New User',
    1: 'Step 1: Contract Upload',
    2: 'Step 2: Gathering Documents',
    3: 'Step 3: Documents Under Review',
    4: 'Step 4: Final Draft Review',
    5: 'Step 5: Documents Submission',
};
const MAX_STEP = Math.max(...Object.keys(STEP_DEFINITIONS).map(Number));

// --- Reusable Input Components (Updated) ---
const AdminFormInput = ({ label, name, type = 'text', value, onChange, required = false, placeholder = '' }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-neutral-300 mb-1">
            {label}{required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            className="w-full bg-neutral-700 border border-neutral-600 rounded-md p-2 focus:ring-[#06b6d4] focus:border-[#06b6d4] text-white"
        />
    </div>
);

// *** MODIFICATION 1: EXPANDED FILE TYPES FOR ADMIN UPLOAD ***
const AdminFileInput = React.forwardRef(({ label, name, onChange, required = false, fileName, accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png" }, ref) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-neutral-300 mb-2">
            {label}{required && <span className="text-red-500">*</span>}
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-600 border-dashed rounded-md">
            <div className="space-y-1 text-center">
                <svg className="mx-auto h-10 w-10 text-neutral-500" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" /></svg>
                <label htmlFor={name} className="relative cursor-pointer bg-neutral-700 hover:bg-neutral-600 rounded-md font-medium text-white px-3 py-1 text-xs">
                    <span>Choose file</span>
                    <input id={name} name={name} type="file" className="sr-only" onChange={onChange} accept={accept} required={required} ref={ref} />
                </label>
                {fileName ? (
                    <p className="text-xs text-green-400 pt-1">{fileName}</p>
                ) : (
                    <p className="text-xs text-neutral-500 pt-1">PDF, DOC, DOCX, JPG, JPEG, PNG up to 10MB</p>
                )}
            </div>
        </div>
    </div>
));
// --- End of Reusable Components ---

// --- Helper function to format date/time ---
const formatNoteTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
        const date = new Date(timestamp);
        return date.toLocaleString(); // Use local format
    } catch (e) {
        console.error("Error formatting timestamp:", e);
        return timestamp; // Fallback
    }
};


// --- Main Admin Page Component ---
const AdminPage = () => {
    // --- State ---
    const [finalContractFile, setFinalContractFile] = useState({});
    const finalContractInputRefs = useRef({});
    const { user: adminUser } = useAuth();
    const [newUser, setNewUser] = useState({ email: '', password: '', role: 'client', firstName: '', lastName: '' });
    const [createUserMessage, setCreateUserMessage] = useState({ type: '', text: '' });
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUserCurrentStep, setSelectedUserCurrentStep] = useState(null);
    const [assignedRequirements, setAssignedRequirements] = useState([]);
    const [stepNotesList, setStepNotesList] = useState([]); // <-- Store list of notes
    const [isLoadingNotes, setIsLoadingNotes] = useState(false); // <-- Loading state for notes
    const [newRequirement, setNewRequirement] = useState({ document_name: '', document_notes: '' });
    const [assignmentMessage, setAssignmentMessage] = useState({ type: '', text: '' });
    const [stepMessage, setStepMessage] = useState({ type: '', text: '' }); // General message for step/note actions
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingRequirements, setIsLoadingRequirements] = useState(false);
    const [isUpdatingStep, setIsUpdatingStep] = useState(false);
    const [rejectionNotes, setRejectionNotes] = useState({});
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(null);
    const [contractFile, setContractFile] = useState(null);
    const [contractFileName, setContractFileName] = useState('');
    const [isUploadingContract, setIsUploadingContract] = useState(false);
    const [contractMessage, setContractMessage] = useState({ type: '', text: '' });
    const contractInputRef = useRef(null);
    const [modalState, setModalState] = useState({ isOpen: false, title: '', message: '', onConfirm: () => { }, confirmText: 'Confirm' });
    const [newContractName, setNewContractName] = useState('');
    const [step1UploadType, setStep1UploadType] = useState('contract'); // 'contract' = upload file, 'request' = name only
    const [isAddingContractRequest, setIsAddingContractRequest] = useState(false);
    const [newStepNoteInput, setNewStepNoteInput] = useState(''); // <-- Input for NEW notes
    const [isAddingStepNote, setIsAddingStepNote] = useState(false); // <-- Loading state for adding note
    const [deleteUserMessage, setDeleteUserMessage] = useState({ type: '', text: '' });
    const [isDeletingUser, setIsDeletingUser] = useState(false);
    // --- End of State ---

    // --- Fetching Functions ---
    const fetchUsers = useCallback(async () => {
        setIsLoadingUsers(true);
        try {
            const response = await fetch('https://renaisons.com/api/get_users.php', { credentials: 'include' });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setUsersList(result.users);
            } else { console.error(`Failed to load users: ${result.message}`); }
        } catch (error) { console.error("Error fetching users:", error); }
        finally { setIsLoadingUsers(false); }
    }, []);
    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    // Fetch requirements (unchanged)
    const fetchUserRequirements = useCallback(async () => {
        if (!selectedUserId) {
            setAssignedRequirements([]);
            return;
        }
        setIsLoadingRequirements(true);
        setAssignmentMessage({ type: '', text: '' });
        setContractMessage({ type: '', text: '' });
        try {
            const response = await fetch(`https://renaisons.com/api/get_user_requirements.php?user_id=${selectedUserId}`, { credentials: 'include' });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setAssignedRequirements(result.requirements);
            } else {
                console.error(`Failed to load requirements: ${result.message}`);
                setAssignedRequirements([]);
            }
        } catch (error) { console.error("Error fetching user requirements:", error); }
        finally { setIsLoadingRequirements(false); }
    }, [selectedUserId]);
    useEffect(() => { fetchUserRequirements(); }, [fetchUserRequirements]);

    // Fetch step notes for the selected user
    const fetchUserNotes = useCallback(async () => {
        if (!selectedUserId) {
            setStepNotesList([]);
            return;
        }
        setIsLoadingNotes(true);
        setStepMessage({ type: '', text: '' }); // Clear message on fetch start
        try {
            const response = await fetch(`https://renaisons.com/api/get_user_step_notes.php?user_id=${selectedUserId}`, {
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setStepNotesList(result.notes || []);
            } else {
                console.error(`Failed to load notes: ${result.message}`);
                setStepMessage({ type: 'error', text: `Failed to load notes: ${result.message || 'Server Error'}` });
                setStepNotesList([]);
            }
        } catch (error) {
            console.error("Error fetching user notes:", error);
            setStepMessage({ type: 'error', text: 'Error fetching notes.' });
            setStepNotesList([]);
        } finally {
            setIsLoadingNotes(false);
        }
    }, [selectedUserId]);
    useEffect(() => { fetchUserNotes(); }, [fetchUserNotes]); // Refetch notes when user changes

    // --- End of Fetching Functions ---

    // --- Handlers for Create User (Unchanged) ---
    const handleNewUserChange = (e) => {
        const { name, value } = e.target;
        setNewUser(prev => ({ ...prev, [name]: value }));
    };
    const handleCreateUserSubmit = async (e) => {
        e.preventDefault();
        setIsCreatingUser(true);
        setCreateUserMessage({ type: '', text: '' });
        try {
            const response = await fetch('https://renaisons.com/api/create_user.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser),
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setCreateUserMessage({ type: 'success', text: `User ${newUser.email} created successfully!` });
                setNewUser({ email: '', password: '', role: 'client', firstName: '', lastName: '' }); // Reset form
                fetchUsers(); // Refresh user list
            } else {
                setCreateUserMessage({ type: 'error', text: result.message || 'Failed to create user.' });
            }
        } catch (error) {
            console.error("Create User Error:", error);
            setCreateUserMessage({ type: 'error', text: 'An error occurred while creating the user.' });
        } finally {
            setIsCreatingUser(false);
        }
    };
    // --- End of Create User Handlers ---
    // --- Handlers for Final Contract Upload ---

    // Handle file change for the specific final contract input
    const handleFinalContractFileChange = (docId, event) => {
        const file = event.target.files[0];
        setContractMessage({ type: '', text: '' });
        if (file) {
            // Use same validation as other uploads (PDF, DOC, DOCX, JPG, JPEG, PNG)
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
            const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
                setContractMessage({ type: 'error', text: 'Invalid file type. Please upload PDF, DOC, DOCX, JPG, JPEG, or PNG.' });
                setFinalContractFile(prev => ({ ...prev, [docId]: null }));
                if (finalContractInputRefs.current[docId]) finalContractInputRefs.current[docId].value = '';
                return;
            }
            setFinalContractFile(prev => ({ ...prev, [docId]: file }));
        } else {
            setFinalContractFile(prev => {
                const newState = { ...prev };
                delete newState[docId];
                return newState;
            });
        }
    };

    // The handler that uploads the final signed contract and sets status to 'approved'
    const handleFinalContractUpload = async (userDocumentId) => {
        const file = finalContractFile[userDocumentId];
        if (!file) {
            setContractMessage({ type: 'error', text: 'Please select the final signed contract file first.' });
            return;
        }

        const contract = assignedRequirements.find(r => r.user_document_id === userDocumentId);
        if (!contract) return;

        setIsUpdatingStatus(userDocumentId); // Set loading state for specific item
        setContractMessage({ type: 'info', text: `Approving and uploading final contract: ${contract.document_name}...` });

        const formData = new FormData();
        formData.append('user_document_id', userDocumentId);
        formData.append('finalSignedFile', file); // Use the name expected by the PHP script

        try {
            // Call the new dedicated endpoint
            const response = await fetch('https://renaisons.com/api/upload_final_contract.php', {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
            const result = await response.json();

            if (response.ok && result.status === 'success') {
                setContractMessage({ type: 'success', text: `Contract ${contract.document_name} approved, final copy uploaded, and user notified!` });
                // Clear state and input field
                setFinalContractFile(prev => { const newState = { ...prev }; delete newState[userDocumentId]; return newState; });
                if (finalContractInputRefs.current[userDocumentId]) finalContractInputRefs.current[userDocumentId].value = '';
                fetchUserRequirements(); // Refresh to show 'approved' status
            } else {
                setContractMessage({ type: 'error', text: `Final upload failed: ${result.message || 'Server error'}` });
            }
        } catch (error) {
            console.error("Error uploading final contract:", error);
            setContractMessage({ type: 'error', text: 'An error occurred during final contract upload.' });
        } finally {
            setIsUpdatingStatus(null);
        }
    };

    // --- Original handleUpdateStatus (Modified to SKIP contract approval) ---
    // This function will now only handle REJECTION for contracts and all actions for 'other' documents.
    const handleUpdateStatus = async (userDocumentId, newStatus) => {
        const contract = assignedRequirements.find(r => r.user_document_id === userDocumentId);

        let notes = null;
        // ... (rest of the handleUpdateStatus remains the same for rejection and other documents)
        // ... (Logic for rejection notes, API call to update_requirement_status.php, etc.)

        // Note: The rest of this function remains for rejection (contracts) and all updates (other documents).

        let notesValue = null;
        if (newStatus === 'rejected') {
            notesValue = rejectionNotes[userDocumentId] || '';
        } else if (newStatus === 'approved') {
            const req = assignedRequirements.find(r => r.user_document_id === userDocumentId);
            notesValue = `${req?.document_name || 'Document'} approved`; // Default approved note
        }

        const setMessage = (selectedUserCurrentStep === 1) ? setContractMessage : setAssignmentMessage;

        // Require notes only for rejection
        if (newStatus === 'rejected' && !notesValue.trim()) {
            setMessage({ type: 'error', text: 'Please enter rejection notes.' });
            return;
        }

        setIsUpdatingStatus(userDocumentId);
        setAssignmentMessage({ type: '', text: '' });
        setContractMessage({ type: '', text: '' });

        try {
            const response = await fetch('https://renaisons.com/api/update_requirement_status.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_document_id: userDocumentId, new_status: newStatus, admin_notes: notesValue }),
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setMessage({ type: 'success', text: `Status updated to ${newStatus}.` });
                setRejectionNotes(prev => ({ ...prev, [userDocumentId]: undefined }));
                fetchUserRequirements();
            } else {
                setMessage({ type: 'error', text: `Failed to update status: ${result.message}` });
            }
        } catch (error) {
            console.error("Error updating status:", error);
            setMessage({ type: 'error', text: 'An error occurred while updating status.' });
        } finally {
            setIsUpdatingStatus(null);
        }
    };
    // --- Modal Controls (Unchanged) ---
    const handleModalClose = () => {
        setModalState({ isOpen: false });
    };

    // --- Handlers for Managing Requirements (Unchanged) ---
    const handleNewRequirementChange = (e) => {
        const { name, value } = e.target;
        setNewRequirement(prev => ({ ...prev, [name]: value }));
    };
    const handleAddRequirement = async (e) => {
        e.preventDefault();
        if (!newRequirement.document_name.trim()) {
            setAssignmentMessage({ type: 'error', text: 'Document name cannot be empty.' });
            return;
        }
        try {
            const response = await fetch('https://renaisons.com/api/add_requirement_to_user.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: selectedUserId, ...newRequirement }),
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setAssignmentMessage({ type: 'success', text: 'Requirement added.' });
                setNewRequirement({ document_name: '', document_notes: '' }); // Reset form
                fetchUserRequirements(); // Refresh requirements list
            } else {
                setAssignmentMessage({ type: 'error', text: `Failed to add: ${result.message}` });
            }
        } catch (error) {
            console.error("Error adding requirement:", error);
            setAssignmentMessage({ type: 'error', text: 'An error occurred while adding the requirement.' });
        }
    };
    const handleRemoveRequirement = (userDocumentId) => {
        const req = assignedRequirements.find(r => r.user_document_id === userDocumentId);
        setModalState({
            isOpen: true,
            title: `Confirm Removal`,
            message: `Are you sure you want to remove "${req?.document_name || 'this requirement'}" for this user? This action cannot be undone.`,
            confirmText: 'Remove',
            onConfirm: () => performRemoveRequirement(userDocumentId)
        });
    };
    const performRemoveRequirement = async (userDocumentId) => {
        handleModalClose();
        const setMessage = (selectedUserCurrentStep === 1) ? setContractMessage : setAssignmentMessage;
        try {
            const response = await fetch('https://renaisons.com/api/remove_requirement.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_document_id: userDocumentId }),
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setMessage({ type: 'success', text: 'Requirement removed.' });
                fetchUserRequirements(); // Refresh requirements list
            } else {
                setMessage({ type: 'error', text: `Failed to remove: ${result.message}` });
            }
        } catch (error) {
            console.error("Error removing requirement:", error);
            setMessage({ type: 'error', text: 'An error occurred while removing the requirement.' });
        }
    };
    const handleRejectionNoteChange = (userDocumentId, value) => {
        setRejectionNotes(prev => ({ ...prev, [userDocumentId]: value }));
    };
    // --- End of Requirement Handlers ---

    // --- Handler for User Selection ---
    const handleUserSelectionChange = (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        // Clear all relevant state when user changes
        setStepMessage({ type: '', text: '' });
        setDeleteUserMessage({ type: '', text: '' });
        setContractMessage({ type: '', text: '' });
        setAssignmentMessage({ type: '', text: '' });
        setContractFile(null);
        setContractFileName('');
        setRejectionNotes({});
        setNewRequirement({ document_name: '', document_notes: '' });
        setNewStepNoteInput(''); // Clear input for adding new notes
        setStepNotesList([]); // Clear notes list
        setSelectedUserCurrentStep(null); // Reset step number
        // Fetch user's current step if a user is selected
        if (userId) {
            const user = usersList.find(u => u.user_id == userId); // Use == for loose comparison if IDs might be string/int mix
            if (user) {
                setSelectedUserCurrentStep(user.onboarding_step);
                // Note: fetchUserNotes and fetchUserRequirements will be triggered by useEffect
            }
        }
    };
    // --- End User Selection Handler ---

    // --- Handler for Adding Step Note ---
    const handleAddStepNote = async () => {
        if (!selectedUserId || !newStepNoteInput.trim() || selectedUserCurrentStep === null) {
            setStepMessage({ type: 'error', text: 'Please select a user and enter a note.' });
            return;
        }

        setIsAddingStepNote(true);
        setStepMessage({ type: 'info', text: 'Adding note...' });

        try {
            const response = await fetch('https://renaisons.com/api/add_step_note.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: selectedUserId,
                    note_content: newStepNoteInput.trim(),
                    step_number: selectedUserCurrentStep // Send the step number this note applies to
                }),
                credentials: 'include'
            });
            const result = await response.json();

            if (response.ok && result.status === 'success') {
                setStepMessage({ type: 'success', text: 'Step note added!' });
                setNewStepNoteInput(''); // Clear the input field
                fetchUserNotes(); // Refresh the notes list to show the new note
            } else {
                setStepMessage({ type: 'error', text: `Failed to add note: ${result.message || 'Server error'}` });
            }
        } catch (error) {
            console.error("Error adding step note:", error);
            setStepMessage({ type: 'error', text: 'An error occurred while adding the note.' });
        } finally {
            setIsAddingStepNote(false);
        }
    };
    // --- End Add Step Note Handler ---

    // --- Handler for Deleting Step Note ---
    const handleDeleteNoteClick = (noteId) => {
        if (!noteId) return;
        // Find the note to show some context in the confirmation message
        const noteToDelete = stepNotesList.find(n => n.note_id === noteId);
        setModalState({
            isOpen: true,
            title: 'Delete Note',
            message: `Are you sure you want to delete this note added on ${formatNoteTimestamp(noteToDelete?.created_at)}? "${noteToDelete?.note_content?.substring(0, 50)}..."`,
            confirmText: 'Delete Note',
            onConfirm: () => performDeleteNote(noteId) // Set the action to perform
        });
    };

    const performDeleteNote = async (noteId) => {
        handleModalClose(); // Close the modal first
        setStepMessage({ type: 'info', text: 'Deleting note...' }); // Show progress message
        try {
            const response = await fetch('https://renaisons.com/api/delete_step_note.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ note_id: noteId }),
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setStepMessage({ type: 'success', text: 'Note deleted.' });
                fetchUserNotes(); // Refresh notes list after deletion
            } else {
                setStepMessage({ type: 'error', text: `Failed to delete note: ${result.message || 'Server error'}` });
            }
        } catch (error) {
            console.error("Error deleting note:", error);
            setStepMessage({ type: 'error', text: 'An error occurred while deleting the note.' });
        }
    };
    // --- End Delete Step Note Handler ---


    // --- Handler for Move to Next Step (Unchanged) ---
    const handleMoveToNextStep = async () => {
        if (!selectedUserId || isUpdatingStep || selectedUserCurrentStep >= MAX_STEP) return;
        // Check if all contracts are approved before moving from step 1
        if (selectedUserCurrentStep === 1) {
            const contracts = assignedRequirements.filter(r => r.document_type === 'contract');
            const allContractsApproved = contracts.length === 0 || contracts.every(c => c.status === 'approved');
            if (!allContractsApproved) {
                setStepMessage({ type: 'error', text: 'All assigned contracts must be approved before moving to Step 2.' });
                return;
            }
        }
        // Check if all *other* requirements are approved before moving from step 2
        if (selectedUserCurrentStep === 2) {
            const requirements = assignedRequirements.filter(r => r.document_type !== 'contract');
            const allRequirementsApproved = requirements.length === 0 || requirements.every(c => c.status === 'approved');
            if (!allRequirementsApproved) {
                setStepMessage({ type: 'error', text: 'All assigned documents for Step 2 must be approved before moving to Step 3.' });
                return;
            }
        }

        const nextStep = selectedUserCurrentStep + 1;
        setIsUpdatingStep(true);
        setStepMessage({ type: '', text: '' }); // Clear previous step messages
        try {
            const response = await fetch('https://renaisons.com/api/update_user_step.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: selectedUserId, new_step: nextStep }),
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setStepMessage({ type: 'success', text: 'User moved to next step!' });
                setSelectedUserCurrentStep(nextStep); // Update local step state immediately
                fetchUsers(); // Refresh user list in dropdown to show new step
                fetchUserNotes(); // Refresh notes for the new step context
            } else {
                setStepMessage({ type: 'error', text: `Failed to update step: ${result.message}` });
            }
        } catch (error) {
            console.error("Error updating step:", error);
            setStepMessage({ type: 'error', text: 'An error occurred while updating the step.' });
        } finally {
            setIsUpdatingStep(false);
        }
    };
    // --- End Move Step Handler ---

    // --- Handlers for Contract Upload (Update to reflect expanded file types) ---
    const handleContractFileChange = (e) => {
        const file = e.target.files[0];
        setContractMessage({ type: '', text: '' });
        if (file) {
            // *** MODIFICATION 2: UPDATED ALLOWED TYPES (match AdminFileInput) ***
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
            const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
                setContractMessage({ type: 'error', text: 'Invalid file type. Please upload PDF, DOC, DOCX, JPG, JPEG, or PNG.' });
                setContractFile(null); setContractFileName('');
                if (contractInputRef.current) contractInputRef.current.value = '';
                return;
            }
            setContractFile(file); setContractFileName(file.name);
        } else {
            setContractFile(null); setContractFileName('');
        }
    };
    const handleContractUpload = async () => {
        if (!selectedUserId || !contractFile || !newContractName.trim()) {
            setContractMessage({ type: 'error', text: 'Please select a user, choose a file, and enter a document name.' });
            return;
        }
        if (isUploadingContract) return;
        setIsUploadingContract(true);
        setContractMessage({ type: 'info', text: 'Uploading document...' });
        const formData = new FormData();
        formData.append('userId', selectedUserId);
        formData.append('contractFile', contractFile);
        formData.append('document_name', newContractName.trim());
        try {
            // Note: This endpoint is used for both blank and signed contracts based on context/naming.
            const response = await fetch('https://renaisons.com/api/upload_admin_contract.php', { method: 'POST', body: formData, credentials: 'include' });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setContractMessage({ type: 'success', text: `Document "${newContractName.trim()}" uploaded successfully!` });
                setContractFile(null); setContractFileName(''); setNewContractName('');
                if (contractInputRef.current) contractInputRef.current.value = '';
                fetchUserRequirements(); // Refresh requirements
            } else {
                setContractMessage({ type: 'error', text: `Upload failed: ${result.message || 'Server error'}` });
            }
        } catch (error) {
            console.error("Error uploading contract:", error);
            setContractMessage({ type: 'error', text: 'An error occurred during upload.' });
        } finally {
            setIsUploadingContract(false);
        }
    };
    // Handler for "Request File" type — creates a Step 1 placeholder requirement the client must upload.
    const handleAddContractRequest = async () => {
        if (!selectedUserId || !newContractName.trim()) {
            setContractMessage({ type: 'error', text: 'Please enter a document name.' });
            return;
        }
        if (isAddingContractRequest) return;
        setIsAddingContractRequest(true);
        setContractMessage({ type: 'info', text: 'Adding request...' });
        try {
            const response = await fetch('https://renaisons.com/api/add_requirement_to_user.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: selectedUserId,
                    document_name: newContractName.trim(),
                    document_notes: '',
                    document_type: 'contract'
                }),
                credentials: 'include'
            });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setContractMessage({ type: 'success', text: `Request "${newContractName.trim()}" added. Awaiting client upload.` });
                setNewContractName('');
                fetchUserRequirements();
            } else {
                setContractMessage({ type: 'error', text: `Failed to add request: ${result.message || 'Server error'}` });
            }
        } catch (error) {
            console.error("Error adding contract request:", error);
            setContractMessage({ type: 'error', text: 'An error occurred while adding the request.' });
        } finally {
            setIsAddingContractRequest(false);
        }
    };
    // --- End of Contract Upload Handlers ---

    // --- Handlers for Delete User (Unchanged) ---
    const handleDeleteUserClick = () => {
        if (!selectedUserId) return;
        const user = usersList.find(u => u.user_id == selectedUserId);
        const userName = user ? (user.first_name || user.email) : 'this user';
        // Prevent admin from deleting themselves
        if (adminUser && user && adminUser.userId === user.user_id) { // Use exact comparison if IDs are consistent type
            setModalState({ isOpen: true, title: 'Action Not Allowed', message: 'You cannot delete your own admin account.', confirmText: 'OK', onConfirm: handleModalClose });
            return;
        }
        setModalState({ isOpen: true, title: `Delete User: ${userName}`, message: `Are you sure you want to permanently delete this user? All their associated data will be erased. This action cannot be undone.`, confirmText: 'Delete User', onConfirm: () => performDeleteUser(user.user_id) });
    };
    const performDeleteUser = async (userIdToDelete) => {
        handleModalClose();
        setIsDeletingUser(true);
        setDeleteUserMessage({ type: 'info', text: 'Deleting user...' });
        try {
            const response = await fetch('https://renaisons.com/api/delete_user.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: userIdToDelete }), credentials: 'include' });
            const result = await response.json();
            if (response.ok && result.status === 'success') {
                setDeleteUserMessage({ type: 'success', text: 'User deleted successfully.' });
                setSelectedUserId(''); // Deselect user
                setSelectedUserCurrentStep(null);
                setAssignedRequirements([]);
                setStepNotesList([]); // Clear notes
                fetchUsers(); // Refresh user list
            } else {
                setDeleteUserMessage({ type: 'error', text: `Failed to delete user: ${result.message}` });
            }
        } catch (error) {
            console.error("Error deleting user:", error);
            setDeleteUserMessage({ type: 'error', text: 'An error occurred while deleting the user.' });
        } finally {
            setIsDeletingUser(false);
        }
    };
    // --- END: Handlers for Delete User ---


    // --- Helper variables (Unchanged) ---
    const currentStepName = STEP_DEFINITIONS[selectedUserCurrentStep] || 'Unknown Step';
    const nextStepName = STEP_DEFINITIONS[selectedUserCurrentStep + 1];
    const atMaxStep = selectedUserCurrentStep >= MAX_STEP;
    const getSelectedUserDisplay = (full = false) => {
        if (!selectedUserId) return '';
        const user = usersList.find(u => u.user_id == selectedUserId); // Use == for loose comparison if IDs might be string/int mix
        if (!user) return '';
        if (user.first_name && user.last_name) {
            return full ? `${user.first_name} ${user.last_name} (${user.email})` : `${user.first_name} ${user.last_name}`;
        }
        return user.email;
    };
    const selectedUserDisplayFull = getSelectedUserDisplay(true);
    const selectedUserDisplayShort = getSelectedUserDisplay(false);
    // Filter requirements based on type
    const step1Contracts = assignedRequirements.filter(req => req.document_type === 'contract');
    const step2Requirements = assignedRequirements.filter(req => req.document_type === 'other');
    // Only clients (role_id = 3) are eligible for status/requirement management
    const clientUsers = usersList.filter(u => Number(u.role_id) === 3);
    // Filter notes for the currently selected user's step
    const notesForCurrentStep = stepNotesList.filter(note => note.step_number === selectedUserCurrentStep);
    // --- End Helper variables ---

    // --- Render Logic ---
    return (
        <div className="p-8 md:p-12 text-white space-y-12">
            <h1 className="text-4xl font-bold">Admin Dashboard</h1>

            {/* Section: Create User */}
            <section className="bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 rounded-2xl border border-white/10">
                <h2 className="text-2xl font-semibold mb-4">Create New User</h2>
                <form onSubmit={handleCreateUserSubmit} className="space-y-4 max-w-lg">
                    <AdminFormInput label="First Name" name="firstName" value={newUser.firstName} onChange={handleNewUserChange} placeholder="John" />
                    <AdminFormInput label="Last Name" name="lastName" value={newUser.lastName} onChange={handleNewUserChange} placeholder="Doe" />
                    <AdminFormInput label="Email" name="email" type="email" value={newUser.email} onChange={handleNewUserChange} required placeholder="newuser@example.com" />
                    <AdminFormInput label="Password" name="password" type="password" value={newUser.password} onChange={handleNewUserChange} required placeholder="Enter temporary password" />
                    <div>
                        <label htmlFor="role" className="block text-sm font-medium text-neutral-300 mb-1">Role</label>
                        <select id="role" name="role" value={newUser.role} onChange={handleNewUserChange} className="w-full bg-neutral-700 border border-neutral-600 rounded-md p-2 focus:ring-[#06b6d4] focus:border-[#06b6d4] text-white">
                            <option value="client">Client</option>
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                        </select>

                    </div>
                    {createUserMessage.text && <p className={`text-sm ${createUserMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>{createUserMessage.text}</p>}
                    <button type="submit" disabled={isCreatingUser} className="bg-[#06b6d4] hover:bg-[#0891b2] text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 transition-colors">
                        {isCreatingUser ? 'Creating...' : 'Create User'}
                    </button>
                </form>
            </section>

            {/* Section: Delete User */}
            <section className="bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 rounded-2xl border border-white/10">
                <h2 className="text-2xl font-semibold mb-4 text-red-400">Delete User Account</h2>
                <div className="mb-4">
                    <label htmlFor="deleteUserSelect" className="block text-sm font-medium text-neutral-300 mb-1">Select User to Delete</label>
                    <select id="deleteUserSelect" value={selectedUserId} onChange={handleUserSelectionChange} disabled={isLoadingUsers} className="w-full md:w-1/2 bg-neutral-700 border border-neutral-600 rounded-md p-2 focus:ring-[#06b6d4] focus:border-[#06b6d4] text-white">
                        <option value="">-- Select a User --</option>
                        {usersList.map(u => (<option key={u.user_id} value={u.user_id}>{u.last_name || u.first_name ? `${u.last_name}, ${u.first_name} (${u.email})` : u.email}</option>))}
                    </select>
                </div>
                {selectedUserId && (
                    <div className="border-t border-red-700/30 pt-4 mt-4">
                        <p className="text-sm text-neutral-400 mb-4">Permanently delete <span className='font-semibold text-white'>{selectedUserDisplayFull}</span> and all their associated data. This action cannot be undone.</p>
                        <button onClick={handleDeleteUserClick} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 transition-colors flex-shrink-0" disabled={!selectedUserId || (adminUser && adminUser.userId == selectedUserId) || isDeletingUser} title={adminUser && adminUser.userId == selectedUserId ? "Cannot delete yourself" : "Delete User"}>
                            {isDeletingUser ? 'Deleting...' : `Delete ${selectedUserDisplayShort ? selectedUserDisplayShort : 'User'}'s Account`}
                        </button>
                    </div>
                )}
                {deleteUserMessage.text && (<p className={`text-sm mt-4 ${deleteUserMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>{deleteUserMessage.text}</p>)}
            </section>

            {/* Section: Manage User Status & Requirements */}
            <section className="bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-6 rounded-2xl border border-white/10">
                <h2 className="text-2xl font-semibold mb-4">Manage User Status & Requirements</h2>
                {/* User Selection */}
                <div className="mb-6">
                    <label htmlFor="userSelect" className="block text-sm font-medium text-neutral-300 mb-1">Select User to Manage</label>
                    <select id="userSelect" value={selectedUserId} onChange={handleUserSelectionChange} disabled={isLoadingUsers} className="w-full md:w-1/2 bg-neutral-700 border border-neutral-600 rounded-md p-2 focus:ring-[#06b6d4] focus:border-[#06b6d4] text-white">
                        <option value="">-- Select a Client --</option>
                        {clientUsers.map(u => (<option key={u.user_id} value={u.user_id}>{u.last_name || u.first_name ? `${u.last_name}, ${u.first_name} (${u.email})` : u.email} (Step {u.onboarding_step})</option>))}
                    </select>
                </div>

                {/* Content for Selected User */}
                {selectedUserId && (
                    <div className="space-y-8">
                        {/* Step Management */}
                        <div className="border border-neutral-700 rounded-lg p-4 space-y-4">
                            <h3 className="text-lg font-medium mb-3 text-neutral-300">User Status: <span className="font-semibold text-white">{selectedUserDisplayFull}</span></h3>
                            <p className="text-md mb-3 text-neutral-200">Current Step: <span className="font-semibold">{currentStepName}</span></p>
                            {/* Display general step/note messages here, except when loading notes */}
                            {stepMessage.text && !isLoadingNotes && (<p className={`text-sm mb-3 ${stepMessage.type === 'error' ? 'text-red-400' : stepMessage.type === 'success' ? 'text-green-400' : 'text-[#06b6d4]'}`}>{stepMessage.text}</p>)}
                            <button onClick={handleMoveToNextStep} disabled={isUpdatingStep || atMaxStep} className="bg-[#06b6d4] hover:bg-[#0891b2] text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                {isUpdatingStep ? 'Moving...' : atMaxStep ? 'At Final Step' : `Move to: ${nextStepName || 'Next Step'}`}
                            </button>
                        </div>

                        {/* Step Notes Section */}
                        <div className="pt-4 border-t border-neutral-600 space-y-4">
                            <h4 className="text-lg font-medium text-neutral-300">
                                Admin Notes for Step {selectedUserCurrentStep} (Visible to User):
                            </h4>
                            {/* Note History */}
                            <div className="space-y-3 max-h-60 overflow-y-auto pr-2 border border-neutral-700 rounded-md p-3 bg-neutral-900/30">
                                {isLoadingNotes ? (
                                    <p className="text-sm text-neutral-400 italic">Loading notes...</p>
                                ) : notesForCurrentStep.length === 0 ? (
                                    <p className="text-sm text-neutral-400 italic">No notes added for this step yet.</p>
                                ) : (
                                    notesForCurrentStep.map(note => (
                                        <div key={note.note_id} className="bg-neutral-700 p-3 rounded-md border border-neutral-600 flex justify-between items-start gap-3">
                                            <div>
                                                <p className="text-sm text-neutral-100 whitespace-pre-wrap">{note.note_content}</p>
                                                <p className="text-xs text-neutral-400 mt-1">{formatNoteTimestamp(note.created_at)}</p>
                                            </div>
                                            {/* Delete Button */}
                                            <button
                                                onClick={() => handleDeleteNoteClick(note.note_id)}
                                                className="text-neutral-400 hover:text-red-400 p-1 rounded flex-shrink-0 disabled:opacity-50"
                                                title="Delete Note"
                                                disabled={stepMessage.type === 'info' && stepMessage.text.includes('Deleting')} // Disable while deleting
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                            {/* Add New Note Area */}
                            <div className="pt-4 border-t border-neutral-700">
                                <label htmlFor="newStepNote" className="block text-sm font-medium text-neutral-300 mb-1">
                                    Add New Note for Step {selectedUserCurrentStep}:
                                </label>
                                <textarea
                                    id="newStepNote"
                                    rows="3"
                                    className="w-full bg-neutral-700 border border-neutral-600 rounded-md p-2 focus:ring-[#06b6d4] focus:border-[#06b6d4] text-white text-sm mb-2"
                                    placeholder={`Enter new note for ${currentStepName}...`}
                                    value={newStepNoteInput}
                                    onChange={(e) => setNewStepNoteInput(e.target.value)}
                                    disabled={isAddingStepNote}
                                />
                                <button
                                    onClick={handleAddStepNote}
                                    disabled={isAddingStepNote || !newStepNoteInput.trim()}
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-3 rounded-md text-sm disabled:opacity-50 transition-colors"
                                >
                                    {isAddingStepNote ? 'Adding...' : 'Add Note'}
                                </button>
                            </div>
                        </div>

                        {/* Step 1: Contracts */}
                        {selectedUserCurrentStep === 1 && (
                            <div className="border border-neutral-700 rounded-lg p-4 space-y-6">
                                <div>
                                    <h3 className="text-lg font-medium text-neutral-300">Step 1: Contracts for {selectedUserDisplayFull}</h3>
                                    {contractMessage.text && (<p className={`text-sm mt-2 p-2 rounded ${contractMessage.type === 'error' ? 'bg-red-900/50 border border-red-700 text-red-300' : contractMessage.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-300' : 'bg-[#06b6d4]/10 border border-[#06b6d4]/40 text-[#67e8f9]'}`}>{contractMessage.text}</p>)}
                                </div>
                                <div className="overflow-x-auto border border-neutral-600 rounded-lg">
                                    <table className="min-w-full divide-y divide-neutral-600">
                                        <thead className="bg-neutral-700"><tr><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">Contract Name</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">Status</th><th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">Review/Actions</th><th scope="col" className="relative px-6 py-3 w-12"><span className="sr-only">Actions</span></th></tr></thead>
                                        <tbody className="bg-neutral-800 divide-y divide-neutral-700">
                                            {isLoadingRequirements && (<tr><td colSpan="4" className="text-center p-4 text-neutral-400">Loading contracts...</td></tr>)}
                                            {!isLoadingRequirements && step1Contracts.length === 0 && (<tr><td colSpan="4" className="text-center p-4 text-neutral-400">No contracts assigned/uploaded.</td></tr>)}
                                            {!isLoadingRequirements && step1Contracts.map((contract) => (
                                                <tr key={contract.user_document_id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{contract.document_name}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-300">
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${contract.status === 'approved' ? 'bg-green-800 text-green-100' : contract.status === 'submitted' ? 'bg-yellow-800 text-yellow-100' : contract.status === 'rejected' ? 'bg-red-800 text-red-100' : 'bg-gray-700 text-gray-100'}`}>
                                                            {contract.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm space-y-2">
                                                        <AdminDocumentLinks
                                                            doc={contract}
                                                            approvedLabel="Final"
                                                            defaultLabel="User Submitted"
                                                            emptyLabel="No User File"
                                                        />

                                                        {/* --- MODIFICATION: REPLACE APPROVE BUTTON WITH FINAL UPLOAD FORM --- */}
                                                        {contract.status === 'submitted' && contract.document_type === 'contract' && (
                                                            <div className="mt-3 p-3 bg-neutral-900/40 border border-neutral-700 rounded-md">
                                                                <p className="text-xs font-semibold text-[#06b6d4] mb-2">REVIEW SUBMITTED DOCUMENTS:</p>

                                                                <div className="flex flex-col gap-3">
                                                                    {/* Action 1: Direct Approval (No File Required) */}
                                                                    <div className="flex flex-col sm:flex-row gap-2 items-center border-b border-neutral-700/50 pb-3">
                                                                        <button
                                                                            onClick={() => handleUpdateStatus(contract.user_document_id, 'approved')}
                                                                            disabled={isUpdatingStatus === contract.user_document_id}
                                                                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-medium rounded disabled:opacity-50 transition-colors w-full sm:w-auto"
                                                                        >
                                                                            {isUpdatingStatus === contract.user_document_id ? 'Approving...' : 'Approve Directly (No File)'}
                                                                        </button>
                                                                        <span className="text-xs text-neutral-400 italic">Use if the user's submitted file is already complete.</span>
                                                                    </div>

                                                                    {/* Action 2: Optional Final Counter-Signed Upload */}
                                                                    <div className="space-y-2">
                                                                        <p className="text-xs font-semibold text-yellow-500">OR UPLOAD:</p>
                                                                        <div className="flex flex-col sm:flex-row gap-2 items-center">
                                                                            <input
                                                                                type="file"
                                                                                id={`final-file-${contract.user_document_id}`}
                                                                                name={`final-file-${contract.user_document_id}`}
                                                                                ref={el => finalContractInputRefs.current[contract.user_document_id] = el}
                                                                                onChange={(e) => handleFinalContractFileChange(contract.user_document_id, e)}
                                                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                                                className="w-full text-xs text-neutral-300 file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-neutral-700 file:text-white hover:file:bg-neutral-600"
                                                                                disabled={isUpdatingStatus === contract.user_document_id}
                                                                            />
                                                                            <button
                                                                                onClick={() => handleFinalContractUpload(contract.user_document_id)}
                                                                                // This button stays disabled UNTIL a file is chosen
                                                                                disabled={!finalContractFile[contract.user_document_id] || isUpdatingStatus === contract.user_document_id}
                                                                                className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 text-xs font-medium rounded disabled:opacity-50 whitespace-nowrap w-full sm:w-auto transition-colors"
                                                                            >
                                                                                {isUpdatingStatus === contract.user_document_id ? 'Uploading...' : 'Approve & Upload'}
                                                                            </button>
                                                                        </div>
                                                                    </div>

                                                                    {/* Action 3: Rejection Panel */}
                                                                    <div className="flex flex-col items-start w-full mt-2 border-t border-neutral-700 pt-3">
                                                                        <button
                                                                            onClick={() => handleUpdateStatus(contract.user_document_id, 'rejected')}
                                                                            disabled={isUpdatingStatus === contract.user_document_id}
                                                                            className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 text-xs rounded disabled:opacity-50 mb-2 transition-colors"
                                                                        >
                                                                            {isUpdatingStatus === contract.user_document_id ? '...' : 'Reject User File'}
                                                                        </button>
                                                                        <textarea
                                                                            value={rejectionNotes[contract.user_document_id] || ''}
                                                                            onChange={(e) => handleRejectionNoteChange(contract.user_document_id, e.target.value)}
                                                                            placeholder="Rejection notes (required if rejecting)"
                                                                            rows="2"
                                                                            className="w-full text-xs bg-neutral-700 border border-neutral-600 rounded p-1.5 focus:ring-[#06b6d4] focus:border-[#06b6d4] text-white placeholder-neutral-500"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {/* --- END MODIFICATION --- */}
                                                        {/* Simple approve/reject for non-contract documents */}
                                                        {contract.status === 'submitted' && contract.document_type !== 'contract' && (
                                                            <div className="mt-3 p-3 bg-neutral-900/40 border border-neutral-700 rounded-md">
                                                                <p className="text-xs font-semibold text-neutral-300 mb-2">REVIEW SUBMITTED DOCUMENT:</p>
                                                                <div className="flex flex-col gap-2">
                                                                    <button
                                                                        onClick={() => handleUpdateStatus(contract.user_document_id, 'approved')}
                                                                        disabled={isUpdatingStatus === contract.user_document_id}
                                                                        className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs rounded disabled:opacity-50"
                                                                    >
                                                                        {isUpdatingStatus === contract.user_document_id ? 'Approving...' : 'Approve'}
                                                                    </button>

                                                                    <div className="flex flex-col items-start w-full mt-2 border-t border-neutral-700 pt-2">
                                                                        <button
                                                                            onClick={() => handleUpdateStatus(contract.user_document_id, 'rejected')}
                                                                            disabled={isUpdatingStatus === contract.user_document_id}
                                                                            className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 text-xs rounded disabled:opacity-50 mb-1"
                                                                        >
                                                                            {isUpdatingStatus === contract.user_document_id ? 'Processing...' : 'Reject Document'}
                                                                        </button>
                                                                        <textarea
                                                                            placeholder="Rejection notes (required)"
                                                                            onChange={(e) => {/* you'll likely already have state for this via rejection notes — reuse it */ }}
                                                                            rows={2}
                                                                            className="mt-1 w-full text-xs p-2 bg-neutral-800 border border-neutral-600 rounded text-white placeholder-neutral-500"
                                                                            disabled={isUpdatingStatus === contract.user_document_id}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {contract.admin_notes && (contract.status === 'rejected' || contract.status === 'approved') && (<p className={`text-xs mt-1 ${contract.status === 'rejected' ? 'text-red-300' : 'text-green-300'}`}>Notes: {contract.admin_notes}</p>)}
                                                        {(contract.status === 'pending') && (<span className="text-neutral-500 text-xs">Awaiting User Upload</span>)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                        <button onClick={() => handleRemoveRequirement(contract.user_document_id)} className="text-red-400 hover:text-red-600" title="Remove Contract">Remove</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="border-t border-neutral-700 pt-6">
                                    <h4 className="text-md font-medium mb-3 text-neutral-300">Add New Document</h4>
                                    {/* Type selector */}
                                    <div className="flex gap-2 mb-4">
                                        <button
                                            type="button"
                                            onClick={() => setStep1UploadType('contract')}
                                            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${step1UploadType === 'contract' ? 'bg-[#06b6d4] text-white' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'}`}
                                        >
                                            Contract (upload file)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setStep1UploadType('request')}
                                            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${step1UploadType === 'request' ? 'bg-[#06b6d4] text-white' : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'}`}
                                        >
                                            Request File (name only)
                                        </button>
                                    </div>
                                    {step1UploadType === 'contract' ? (
                                        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
                                            <div className="flex-grow w-full"><AdminFormInput label="Document Name" name="newContractName" value={newContractName} onChange={(e) => setNewContractName(e.target.value)} placeholder="e.g., Master Service Agreement" required /></div>
                                            <div className="flex-grow w-full"><AdminFileInput label="Document File" name="contractFile" onChange={handleContractFileChange} fileName={contractFileName} required ref={contractInputRef} /></div>
                                            <button type="button" onClick={handleContractUpload} disabled={isUploadingContract || !contractFile || !newContractName.trim()} className="bg-[#06b6d4] hover:bg-[#0891b2] text-white font-bold py-2 px-4 rounded-md whitespace-nowrap w-full md:w-auto disabled:opacity-50 flex-shrink-0 transition-colors">{isUploadingContract ? 'Uploading...' : 'Upload Document'}</button>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
                                            <div className="flex-grow w-full"><AdminFormInput label="Document Name" name="newContractName" value={newContractName} onChange={(e) => setNewContractName(e.target.value)} placeholder="e.g., Signed NDA — client will upload" required /></div>
                                            <button type="button" onClick={handleAddContractRequest} disabled={isAddingContractRequest || !newContractName.trim()} className="bg-[#06b6d4] hover:bg-[#0891b2] text-white font-bold py-2 px-4 rounded-md whitespace-nowrap w-full md:w-auto disabled:opacity-50 flex-shrink-0 transition-colors">{isAddingContractRequest ? 'Adding...' : 'Add Request'}</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 2: Requirements */}
                        {/* Step 2: Requirements */}
                        {selectedUserCurrentStep === 2 && (
                            <div>
                                <h3 className="text-lg font-medium mb-2 text-neutral-300">Step 2: Assigned Requirements for {selectedUserDisplayFull}</h3>
                                {assignmentMessage.text && (<p className={`text-sm mb-4 p-3 rounded ${assignmentMessage.type === 'error' ? 'bg-red-900/50 border border-red-700 text-red-300' : 'bg-green-900/50 border border-green-700 text-green-300'}`}>{assignmentMessage.text}</p>)}
                                <div className="overflow-x-auto mb-6 border border-neutral-600 rounded-lg">
                                    <table className="min-w-full divide-y divide-neutral-600">
                                        <thead className="bg-neutral-700">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">Document Name</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">Notes</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">Status</th>
                                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">Review/Actions</th>
                                                <th scope="col" className="relative px-6 py-3 w-12"><span className="sr-only">Actions</span></th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-neutral-800 divide-y divide-neutral-700">
                                            {isLoadingRequirements && (<tr><td colSpan="5" className="text-center p-4 text-neutral-400">Loading requirements...</td></tr>)}
                                            {!isLoadingRequirements && step2Requirements.length === 0 && (<tr><td colSpan="5" className="text-center p-4 text-neutral-400">No requirements assigned for this step.</td></tr>)}

                                            {!isLoadingRequirements && step2Requirements.map((req) => (
                                                <tr key={req.user_document_id}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{req.document_name}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-300">{req.document_notes}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-300">
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${req.status === 'approved' ? 'bg-green-800 text-green-100' : req.status === 'submitted' ? 'bg-yellow-800 text-yellow-100' : req.status === 'rejected' ? 'bg-red-800 text-red-100' : 'bg-gray-700 text-gray-100'}`}>
                                                            {req.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 whitespace-nowrap text-sm space-y-2">
                                                        <AdminDocumentLinks doc={req} approvedLabel="" defaultLabel="" emptyLabel="No File" />

                                                        {/* CLEANED UP REGULAR DOCUMENT ACTION PANEL (NO FILE UPLOAD REQUIRED) */}
                                                        {req.status === 'submitted' && (
                                                            <div className="mt-3 p-3 bg-neutral-900/40 border border-neutral-700 rounded-md">
                                                                <p className="text-xs font-semibold text-neutral-300 mb-2">REVIEW SUBMITTED DOCUMENT:</p>
                                                                <div className="flex flex-col gap-2">
                                                                    <button
                                                                        onClick={() => handleUpdateStatus(req.user_document_id, 'approved')}
                                                                        disabled={isUpdatingStatus === req.user_document_id}
                                                                        className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs rounded disabled:opacity-50 w-full sm:w-auto self-start"
                                                                    >
                                                                        {isUpdatingStatus === req.user_document_id ? 'Approving...' : 'Approve'}
                                                                    </button>

                                                                    <div className="flex flex-col items-start w-full mt-1 border-t border-neutral-700 pt-2">
                                                                        <button
                                                                            onClick={() => handleUpdateStatus(req.user_document_id, 'rejected')}
                                                                            disabled={isUpdatingStatus === req.user_document_id}
                                                                            className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 text-xs rounded disabled:opacity-50 mb-1"
                                                                        >
                                                                            {isUpdatingStatus === req.user_document_id ? 'Rejecting...' : 'Reject'}
                                                                        </button>
                                                                        <textarea
                                                                            value={rejectionNotes[req.user_document_id] || ''}
                                                                            onChange={(e) => handleRejectionNoteChange(req.user_document_id, e.target.value)}
                                                                            placeholder="Rejection notes (required if rejecting)"
                                                                            rows="2"
                                                                            className="w-full text-xs bg-neutral-700 border border-neutral-600 rounded p-1 focus:ring-[#06b6d4] focus:border-[#06b6d4] text-white placeholder-neutral-500"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {req.status === 'rejected' && req.admin_notes && (<p className="text-xs text-red-300 mt-1">Notes: {req.admin_notes}</p>)}
                                                        {req.status === 'approved' && req.admin_notes && (<p className="text-xs text-green-300 mt-1">Notes: {req.admin_notes}</p>)}
                                                        {req.status === 'pending' && (<span className="text-neutral-500 text-xs">Awaiting User Upload</span>)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                        <button onClick={() => handleRemoveRequirement(req.user_document_id)} className="text-red-400 hover:text-red-600" title="Remove Requirement">Remove</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Add New Requirement Form */}
                                <form onSubmit={handleAddRequirement} className="border-t border-neutral-700 pt-6">
                                    <h4 className="text-md font-medium mb-2 text-neutral-300">Add New Requirement for {selectedUserDisplayFull}</h4>
                                    <div className="flex flex-col md:flex-row gap-4 items-end">
                                        <div className="flex-grow w-full"><AdminFormInput label="Document Name" name="document_name" value={newRequirement.document_name} onChange={handleNewRequirementChange} placeholder="e.g., ID Card, Passport" required /></div>
                                        <div className="flex-grow w-full"><AdminFormInput label="Notes (Optional)" name="document_notes" value={newRequirement.document_notes} onChange={handleNewRequirementChange} placeholder="e.g., Must be valid for 6 months" /></div>
                                        <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md whitespace-nowrap w-full md:w-auto">Add Requirement</button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Placeholder for Steps 3+ */}
                        {selectedUserCurrentStep >= 3 && (
                            <div className="border border-neutral-700 rounded-lg p-4">
                                <h3 className="text-lg font-medium text-neutral-300">{STEP_DEFINITIONS[selectedUserCurrentStep]} for {selectedUserDisplayFull}</h3>
                                <p className="text-neutral-400">Review status of documents for this step.</p>
                                {/* Display relevant requirements/contracts here */}
                                <div className="space-y-4 mt-4">
                                    {[...step1Contracts, ...step2Requirements] // Example: show all docs
                                        .sort((a, b) => a.document_name.localeCompare(b.document_name))
                                        .map(doc => (
                                            <div key={doc.user_document_id} className="flex justify-between items-center p-3 bg-neutral-900/50 border border-neutral-700 rounded">
                                                <span className="text-white">{doc.document_name}</span>
                                                <span className={`px-2 py-0.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${doc.status === 'approved' ? 'bg-green-800 text-green-100' : 'bg-gray-700 text-gray-100'}`}>
                                                    {/* Display Check or Clock icon based on status */}
                                                    {doc.status === 'approved' ? <CheckCircleIcon className="h-4 w-4 mr-1.5" /> : <ClockIcon className="h-4 w-4 mr-1.5" />} {doc.status}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Modal Component */}
            <ConfirmModal isOpen={modalState.isOpen} onClose={handleModalClose} onConfirm={modalState.onConfirm} title={modalState.title} message={modalState.message} confirmText={modalState.confirmText} />

        </div>
    );
};

export default AdminPage;